import { useState } from 'react';
import {
  saudiGallery,
  turkeyGallery,
  uaeGallery,
  moroccoGallery,
  egyptGallery,
  jordanGallery,
  malaysiaGallery,
  indonesiaGallery,
  maldivesGallery,
  bosniaGallery,
} from '../../../mocks/destination-galleries';

interface GalleryPhoto {
  id: number;
  image: string;
  title: string;
  size: string;
}

interface GalleryCardProps {
  photo: GalleryPhoto;
  onClick: () => void;
}

interface LightboxModalProps {
  photos: GalleryPhoto[];
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

interface PhotoGalleryProps {
  selectedCountry: string;
}

const countryLabels: Record<string, string> = {
  'saudi-arabia': 'Saudi Arabia',
  turkey: 'Turkey',
  uae: 'UAE',
  morocco: 'Morocco',
  egypt: 'Egypt',
  jordan: 'Jordan',
  malaysia: 'Malaysia',
  indonesia: 'Indonesia',
  maldives: 'Maldives',
  bosnia: 'Bosnia & Herzegovina',
};

function GalleryCard({ photo, onClick }: GalleryCardProps) {
  const [imgError, setImgError] = useState(false);

  const sizeClass =
    photo.size === 'wide'
      ? 'col-span-2 row-span-1'
      : photo.size === 'tall'
      ? 'col-span-1 row-span-2'
      : 'col-span-1 row-span-1';

  const heightClass =
    photo.size === 'wide' ? 'h-56' : photo.size === 'tall' ? 'h-full min-h-[28rem]' : 'h-56';

  return (
    <div
      className={`${sizeClass} relative overflow-hidden rounded-2xl cursor-pointer group`}
      onClick={onClick}
    >
      <div className={`${heightClass} w-full bg-gray-100`}>
        {!imgError ? (
          <img
            src={photo.image}
            alt={photo.title}
            loading="lazy"
            onError={() => setImgError(true)}
            className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#f5f0e8] to-[#e8dcc8]">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-image-line text-[#C9A961] text-4xl"></i>
            </div>
          </div>
        )}
      </div>

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center gap-2">
          <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full backdrop-blur-sm">
            <i className="ri-zoom-in-line text-white text-xl"></i>
          </div>
        </div>
      </div>

      {/* Title label */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
        <p className="text-white text-sm font-semibold">{photo.title}</p>
      </div>
    </div>
  );
}

function LightboxModal({ photos, currentIndex, onClose, onPrev, onNext }: LightboxModalProps) {
  const current = photos[currentIndex];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-all cursor-pointer z-10"
        onClick={onClose}
      >
        <i className="ri-close-line text-2xl"></i>
      </button>

      {/* Counter */}
      <div className="absolute top-5 left-1/2 -translate-x-1/2 bg-white/10 text-white text-sm px-4 py-1.5 rounded-full backdrop-blur-sm">
        {currentIndex + 1} / {photos.length}
      </div>

      {/* Prev button */}
      <button
        className="absolute left-4 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-all cursor-pointer z-10"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
      >
        <i className="ri-arrow-left-s-line text-3xl"></i>
      </button>

      {/* Image */}
      <div
        className="max-w-5xl max-h-[80vh] mx-16 flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={current.image}
          alt={current.title}
          className="max-w-full max-h-[72vh] object-contain rounded-xl shadow-2xl"
        />
        <p className="text-white text-lg font-semibold tracking-wide">{current.title}</p>
      </div>

      {/* Next button */}
      <button
        className="absolute right-4 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-all cursor-pointer z-10"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
      >
        <i className="ri-arrow-right-s-line text-3xl"></i>
      </button>
    </div>
  );
}

export default function PhotoGallery({ selectedCountry }: PhotoGalleryProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const getGallery = (): GalleryPhoto[] => {
    switch (selectedCountry) {
      case 'saudi-arabia': return saudiGallery;
      case 'turkey': return turkeyGallery;
      case 'uae': return uaeGallery;
      case 'morocco': return moroccoGallery;
      case 'egypt': return egyptGallery;
      case 'jordan': return jordanGallery;
      case 'malaysia': return malaysiaGallery;
      case 'indonesia': return indonesiaGallery;
      case 'maldives': return maldivesGallery;
      case 'bosnia': return bosniaGallery;
      default: return saudiGallery;
    }
  };

  const gallery = getGallery();
  const countryName = countryLabels[selectedCountry] || 'Saudi Arabia';

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const goToPrevious = () =>
    setCurrentImageIndex((prev) => (prev === 0 ? gallery.length - 1 : prev - 1));

  const goToNext = () =>
    setCurrentImageIndex((prev) => (prev === gallery.length - 1 ? 0 : prev + 1));

  return (
    <section className="py-20 bg-[#faf8f5]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block text-[#C9A961] text-sm font-semibold tracking-widest uppercase mb-3">
            Photo Gallery
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#1A1F2E] mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Discover {countryName} Through Our Lens
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            A curated visual journey through the most breathtaking sights, landscapes, and cultural gems
          </p>
        </div>

        {/* Masonry-style grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-auto">
          {gallery.map((photo, index) => (
            <GalleryCard
              key={photo.id}
              photo={photo}
              onClick={() => openLightbox(index)}
            />
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <button
            onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#C9A961] text-white rounded-full font-semibold hover:bg-[#B89951] transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-line"></i>
            Plan Your {countryName} Trip
          </button>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <LightboxModal
          photos={gallery}
          currentIndex={currentImageIndex}
          onClose={closeLightbox}
          onPrev={goToPrevious}
          onNext={goToNext}
        />
      )}
    </section>
  );
}
